import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CheckCircle2, Phone, Clock, Loader2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Email } from "@/lib/email-service";
import { Link } from "react-router-dom";

const formSchema = z.object({
  firstName: z.string().min(2, "Voornaam is verplicht"),
  lastName: z.string().min(2, "Achternaam is verplicht"),
  email: z.string().email("Ongeldig e-mailadres"),
  phone: z.string().min(10, "Geldig telefoonnummer is verplicht"),
  address: z.string().min(5, "Adres is verplicht"),
  serviceType: z.string().min(1, "Kies een dienst"),
  message: z.string().optional(),
});

const Quote = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      serviceType: "",
      message: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    
    const serviceMap: Record<string, string> = {
      airco: "Airconditioning",
      warmtepomp: "Warmtepomp",
      zon_elektra: "Zonnepanelen & Elektra",
      cv_sanitair: "CV & Sanitair",
      maintenance: "Onderhoud & Service",
      other: "Anders"
    };
    const serviceName = serviceMap[values.serviceType] || values.serviceType;

    try {
      // Simpele, robuuste e-mail call. Zonder replyTo of from velden die vaak door 
      // externe servers (zoals Netlify) of spamfilters worden geblokkeerd.
      await Email.send({
        to: ["spiro.schuring@gmail.com"],
        subject: `Nieuwe Offerte: ${values.firstName} ${values.lastName} - ${serviceName}`,
        html: `
          <div style="font-family: sans-serif; max-width: 600px; margin: 0 auto; background: #fff; padding: 20px; border: 1px solid #eee; border-radius: 8px;">
            <h2 style="color: #ea580c; border-bottom: 2px solid #ea580c; padding-bottom: 10px;">Nieuwe Aanvraag (SpiroTechniek)</h2>
            <p><strong>Naam:</strong> ${values.firstName} ${values.lastName}</p>
            <p><strong>E-mailadres:</strong> ${values.email}</p>
            <p><strong>Telefoon:</strong> ${values.phone}</p>
            <p><strong>Adres:</strong> ${values.address}</p>
            <p><strong>Gewenste dienst:</strong> ${serviceName}</p>
            <div style="background: #f9f9f9; padding: 15px; border-left: 4px solid #cbd5e1; margin-top: 20px;">
              <strong>Bericht van klant:</strong><br/>
              ${values.message ? values.message.replace(/\n/g, '<br/>') : '<em>Geen extra bericht.</em>'}
            </div>
          </div>
        `,
        text: `Nieuwe Aanvraag (SpiroTechniek)\n\nNaam: ${values.firstName} ${values.lastName}\nE-mail: ${values.email}\nTelefoon: ${values.phone}\nAdres: ${values.address}\nDienst: ${serviceName}\n\nBericht: ${values.message || 'Geen extra bericht'}`
      });

      setIsSubmitted(true);
      toast.success("Aanvraag succesvol verstuurd!");
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (error) {
      console.error("Email verzend fout:", error);
      toast.error("Er ging iets mis bij het versturen. Probeer het later opnieuw of bel ons direct.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Left Column: Context & Trust */}
          <div>
            <div className="inline-block bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-semibold mb-4">
              Vrijblijvende offerte
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              Start uw project met <span className="text-orange-600">SpiroTechniek</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Vul het formulier in en we nemen zo snel mogelijk contact op. Geen verplichtingen, wel eerlijk advies.
            </p>

            <div className="space-y-6 mb-12">
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                   <Clock className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Snelle reactie</p>
                  <p className="text-sm text-gray-500">Snel antwoord op uw aanvraag</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                   <CheckCircle2 className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-bold text-gray-900">Vrijblijvend advies</p>
                  <p className="text-sm text-gray-500">Gratis opname op locatie mogelijk</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
              <h3 className="font-semibold mb-4">Liever direct bellen?</h3>
              <p className="text-gray-600 mb-4 text-sm">Voor spoed of directe vragen zijn wij telefonisch bereikbaar.</p>
              <a href="tel:+31658850981" className="flex items-center gap-2 text-xl font-bold text-gray-900 hover:text-orange-600 transition-colors">
                <Phone className="h-5 w-5" /> 06 588 509 81
              </a>
            </div>
          </div>

          {/* Right Column: Form or Success Message */}
          <div>
            <Card className="shadow-xl border-t-4 border-t-orange-600 overflow-hidden">
              {isSubmitted ? (
                <CardContent className="pt-16 pb-16 text-center flex flex-col items-center justify-center min-h-[500px]">
                  <div className="mb-6">
                    <div className="bg-green-100 p-5 rounded-full inline-flex animate-in zoom-in duration-500">
                      <CheckCircle2 className="h-16 w-16 text-green-600" />
                    </div>
                  </div>
                  <CardTitle className="text-3xl font-bold mb-4 text-gray-900">Aanvraag Verstuurd!</CardTitle>
                  <p className="text-gray-600 mb-10 max-w-sm text-lg">
                    Bedankt voor uw aanvraag. We hebben uw gegevens succesvol ontvangen en nemen zo snel mogelijk contact met u op.
                  </p>
                  <Button asChild className="bg-gray-900 text-white hover:bg-gray-800 px-8 py-6 h-auto text-lg">
                    <Link to="/">
                      <ArrowLeft className="mr-2 h-5 w-5" /> Terug naar Home
                    </Link>
                  </Button>
                </CardContent>
              ) : (
                <>
                  <CardHeader>
                    <CardTitle>Aanvraagformulier</CardTitle>
                    <CardDescription>Vul uw gegevens in voor een passende offerte.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Form {...form}>
                      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <FormField
                            control={form.control}
                            name="firstName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Voornaam*</FormLabel>
                                <FormControl>
                                  <Input placeholder="Jan" {...field} disabled={isSubmitting} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            control={form.control}
                            name="lastName"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Achternaam*</FormLabel>
                                <FormControl>
                                  <Input placeholder="Jansen" {...field} disabled={isSubmitting} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>E-mailadres*</FormLabel>
                              <FormControl>
                                <Input type="email" placeholder="jan@voorbeeld.nl" {...field} disabled={isSubmitting} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Telefoonnummer*</FormLabel>
                              <FormControl>
                                <Input type="tel" placeholder="06 12345678" {...field} disabled={isSubmitting} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="address"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Adres + Woonplaats*</FormLabel>
                              <FormControl>
                                <Input placeholder="Straatnaam 1, Lelystad" {...field} disabled={isSubmitting} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="serviceType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Waar heeft u interesse in?*</FormLabel>
                              <Select onValueChange={field.onChange} defaultValue={field.value} disabled={isSubmitting}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selecteer een dienst" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  <SelectItem value="airco">Airconditioning</SelectItem>
                                  <SelectItem value="warmtepomp">Warmtepomp</SelectItem>
                                  <SelectItem value="zon_elektra">Zonnepanelen & Elektra</SelectItem>
                                  <SelectItem value="cv_sanitair">CV & Sanitair</SelectItem>
                                  <SelectItem value="maintenance">Onderhoud & Service</SelectItem>
                                  <SelectItem value="other">Anders</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="message"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Uw bericht (optioneel)</FormLabel>
                              <FormControl>
                                <Textarea 
                                  placeholder="Bijv: Ik wil graag 2 slaapkamers en de woonkamer koelen."
                                  className="min-h-[100px]"
                                  {...field} 
                                  disabled={isSubmitting}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <Button type="submit" disabled={isSubmitting} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold h-14 text-lg shadow-md transition-all">
                          {isSubmitting ? (
                            <>
                              <Loader2 className="mr-2 h-6 w-6 animate-spin" />
                              Versturen...
                            </>
                          ) : (
                            "Verstuur Aanvraag"
                          )}
                        </Button>
                      </form>
                    </Form>
                  </CardContent>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quote;
